import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, CircularProgress, Stack
} from "@mui/material";
import { Print, Search } from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { format, getDaysInMonth, startOfMonth, endOfMonth, parseISO } from "date-fns";

function Attendancereport() {
    const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [attendanceMap, setAttendanceMap] = useState({});

    // Calculate days in the selected month
    const year = parseInt(selectedMonth.split("-")[0]);
    const month = parseInt(selectedMonth.split("-")[1]) - 1;
    const daysInMonth = getDaysInMonth(new Date(year, month));
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch all staff
            const staffSnap = await getDocs(collection(db, "staff"));
            const staffData = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setStaffList(staffData);

            // 2. Fetch attendance for the month
            const start = format(startOfMonth(new Date(year, month)), "yyyy-MM-dd");
            const end = format(endOfMonth(new Date(year, month)), "yyyy-MM-dd");

            const q = query(
                collection(db, "staffattendance"),
                where("date", ">=", start),
                where("date", "<=", end)
            );

            const attSnap = await getDocs(q);
            const map = {};
            attSnap.docs.forEach(doc => {
                const data = doc.data();
                const key = `${data.staffId}_${data.date}`;
                map[key] = data.status;
            });
            setAttendanceMap(map);
        } catch (error) {
            console.error("Error:", error);
        }
        setLoading(false);
    };

    useEffect(() => { fetchData(); }, []);

    const getStatusChar = (staffId, day) => {
        const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
        const status = attendanceMap[`${staffId}_${dateStr}`];
        if (status === "Present") return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>P</span>;
        if (status === "Absent") return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>A</span>;
        if (status === "Half Day") return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>H</span>;
        return "-";
    };

    const calculateTotal = (staffId) => {
        let p = 0, h = 0, a = 0;
        daysArray.forEach(day => {
            const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
            const status = attendanceMap[`${staffId}_dateStr` === `${staffId}_${dateStr}` ? `${staffId}_${dateStr}` : ""];
            const res = attendanceMap[`${staffId}_${dateStr}`];
            if (res === "Present") p++;
            if (res === "Half Day") h++;
            if (res === "Absent") a++;
        });
        return { p, a, h, total: p + (h * 0.5) };
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} className="no-print">
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Select Month" type="month" size="small"
                        value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={fetchData} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Load Report"}
                    </Button>
                    {/* <Button variant="outlined" onClick={() => window.print()} startIcon={<Print />}>Print</Button> */}
                </Stack>
            </Paper>

            <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #ccc' }}>
                <Box textAlign="center" p={2}>
                    <Typography variant="h5" fontWeight="bold">Vishwajeet Khadi Kendra</Typography>
                    <Typography variant="body2">Dharashiv, Maharashtra</Typography>
                    <Typography variant="subtitle1">Monthly Attendance Report - {format(new Date(year, month), "MMMM yyyy")}</Typography>
                </Box>
                <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={headerCell}>Sr.</TableCell>
                            <TableCell sx={{ ...headerCell, minWidth: 150, position: 'sticky', left: 0, zIndex: 10, bgcolor: '#f5f5f5' }}>Staff Name</TableCell>
                            {daysArray.map(d => (
                                <TableCell key={d} align="center" sx={dayCell}>{d}</TableCell>
                            ))}
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e8f5e9' }}>P</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#fff3e0' }}>H</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#ffebee' }}>A</TableCell>
                            <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e3f2fd' }}>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {staffList.map((staff, index) => {
                            const stats = calculateTotal(staff.id);
                            return (
                                <TableRow key={staff.id} hover>
                                    <TableCell align="center">{index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 5, bgcolor: '#fff' }}>{staff.name}</TableCell>
                                    {daysArray.map(d => (
                                        <TableCell key={d} align="center" sx={dayCell}>
                                            {getStatusChar(staff.id, d)}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{stats.p}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{stats.h}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'red' }}>{stats.a}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f0f7ff' }}>{stats.total}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { margin: 0; padding: 0; zoom: 70%; }
                    @page { size: A4 landscape; margin: 5mm; }
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border: 1px solid #000 !important; font-size: 10px !important; padding: 2px !important; }
                }
            `}</style>
        </Box>
    );
}

const headerCell = { fontWeight: 'bold', border: '1px solid #ddd', fontSize: '0.75rem', p: 1 };
const dayCell = { border: '1px solid #ddd', p: 0.5, fontSize: '0.7rem', minWidth: '25px' };

export default Attendancereport;